"use client";

import React, { createContext, useContext, useMemo } from "react";

import useSessionExpirationCheck from "./useSessionExpirationCheck";
import { useLocalStorageWithListener } from "../../components/utilities/useLocalStorage";
import useCookie from "../../components/utilities/useCookie";
import { useEnvironmentVariables } from "../environmentVariables";

interface Session {
	token: string;
	expires: string;
	mfa: {
		required: boolean;
		challengeToken: string;
	};
}

interface SessionContextValue {
	sessionToken: string;
	hasAuthenticatedSession: boolean;
	hasSessionExpired: boolean;
	isMfaRequired: boolean;
	mfaChallengeToken: string;
	createSession: (params?: { token?: string; expires?: string }) => void;
	removeSession: () => void;
	expireSession: () => void;
	setMfaChallenge: (params: { challengeToken: string }) => void;
	clearMfaChallenge: () => void;
}

const SESSION_STORAGE_KEY = "session";
const sessionContext = createContext<SessionContextValue | undefined>(
	undefined,
);

export function useSession(): SessionContextValue {
	const context = useContext(sessionContext);

	if (!context) {
		throw new Error(
			"useSession should always be used within a SessionProvider",
		);
	}

	return context;
}

interface SessionProviderProps {
	children: React.ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({
	children,
	...rest
}) => {
	const [session = {}, setSession] = useLocalStorageWithListener(
		SESSION_STORAGE_KEY,
		{
			token: "",
			expires: "",
			mfa: {
				required: false,
				challengeToken: "",
			},
		},
	);

	const { cookieDomain } = useEnvironmentVariables();
	const [, setCookie, removeCookie] = useCookie("jasper_is_authenticated");
	const hasSessionExpired = useSessionExpirationCheck(session.expires);

	const value = useMemo(() => {
		const sessionToken = session.token;
		const hasAuthenticatedSession = Boolean(sessionToken);
		const isMfaRequired = session?.mfa?.required ?? false;
		const mfaChallengeToken = session?.mfa?.challengeToken ?? "";

		return {
			sessionToken,
			hasAuthenticatedSession,
			hasSessionExpired,
			isMfaRequired,
			mfaChallengeToken,

			createSession({ token = "", expires = "" } = {}) {
				setSession({
					token,
					expires,
					mfa: {
						required: false,
						challengeToken: "",
					},
				});
				setCookie(1, { expires: new Date(expires), domain: cookieDomain });
			},

			removeSession() {
				if (hasAuthenticatedSession) {
					setSession({
						token: "",
						expires: "",
						mfa: {
							required: false,
							challengeToken: "",
						},
					});
					removeCookie({ domain: cookieDomain });
				}
			},

			expireSession() {
				if (session.expires) {
					setSession((s: Session) => ({ ...s, expires: "" }));
				}
			},

			setMfaChallenge({ challengeToken }: { challengeToken: string }) {
				setSession((s: Session) => ({
					...s,
					mfa: {
						required: true,
						challengeToken,
					},
				}));
			},

			clearMfaChallenge() {
				setSession((s: Session) => ({
					...s,
					mfa: {
						required: false,
						challengeToken: "",
					},
				}));
			},
		};
	}, [
		session,
		hasSessionExpired,
		setSession,
		setCookie,
		removeCookie,
		cookieDomain,
	]);

	return (
		<sessionContext.Provider value={value} {...rest}>
			{children}
		</sessionContext.Provider>
	);
};
